import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { dashboard } from '@/api/dashboard';
import type { RecentActivityItem } from '@/api/dashboard';
import { callSessions } from '@/api/callSessions';
import type { CallSessionRow } from '@/api/callSessions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { Card, CardContent } from '@/components/ui/card';
import {
  PhoneCall, Mic, ShoppingCart, GraduationCap, Megaphone,
  MessageSquare, Users, ArrowRight, Activity, Coins,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { GlobalSearchPanel } from '@/components/search/GlobalSearchPanel';

export function Dashboard() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const tenant = useAuth((s) => s.tenant);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboard.summary(),
    // 10s — live-calls metric drives a "currently in progress" tile that
    // the operator wants to feel real-time. The other metrics on the
    // payload are slower-moving but the cost of refreshing them more
    // often is negligible (one round-trip; per-metric query is a fast
    // COUNT under RLS).
    refetchInterval: 10_000,
  });

  const { data: activityData } = useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: () => dashboard.activity(),
    refetchInterval: 60_000,
  });

  const s = summary ?? {
    calls_today: 0, calls_this_month: 0, calls_live: 0, voicemails_unread: 0,
    orders_open: 0, enrollments_active: 0, forum_pending: 0,
    sms_today_outbound: 0, sms_today_inbound: 0, callers_total: 0,
  };

  const greeting = greetingFor(new Date(), t);
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });

  return (
    <>
      <header className="mb-8 pb-6 border-b-2 border-slate-900">
        <div className="flex items-center justify-between mb-2">
          <time className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
            {today}
          </time>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
            {tenantLabel(tenant, t)}
          </span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
          {greeting},{' '}
          <span className="text-primary">{user.display_name ?? user.email.split('@')[0]}</span>
        </h1>
      </header>

      <section className="mb-6">
        <GlobalSearchPanel />
      </section>

      <section className="mb-8">
        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
          <HeroStat
            icon={PhoneCall}
            label={t('dashboard.callsToday')}
            value={s.calls_today}
            sublabel={t('dashboard.thisMonth', { count: s.calls_this_month.toLocaleString() })}
            loading={isLoading}
          />
          <div className="grid grid-cols-2 gap-4">
            <LiveCallsStat value={s.calls_live} />
            <InlineStat
              icon={MessageSquare}
              label={t('dashboard.smsToday')}
              value={s.sms_today_outbound + s.sms_today_inbound}
              sublabel={`${s.sms_today_outbound} out · ${s.sms_today_inbound} in`}
            />
            <InlineStat
              icon={Users}
              label={t('dashboard.callersOnFile')}
              value={s.callers_total}
              sublabel={t('dashboard.lifetime')}
            />
            <InlineStat
              icon={GraduationCap}
              label={t('dashboard.activeEnrollments')}
              value={s.enrollments_active}
              sublabel={t('dashboard.inProgress')}
            />
            <InlineStat
              icon={ShoppingCart}
              label={t('dashboard.openOrders')}
              value={s.orders_open}
              sublabel={t('dashboard.cartAndSubmitted')}
            />
          </div>
        </div>
      </section>

      <LiveNowSection liveCount={s.calls_live} />

      <section className="grid md:grid-cols-[18rem_minmax(0,1fr)] gap-6 mb-8">
        <AttentionCard summary={s} />
        <ActivityFeed items={activityData?.rows ?? []} />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <QuickAction to="/ivr/trees" label={t('dashboard.quickActions.ivrTrees')} />
        <QuickAction to="/caller-profiles" label={t('dashboard.quickActions.callers')} />
        <QuickAction to="/recordings" label={t('dashboard.quickActions.recordings')} />
        <QuickAction to="/docs" label={t('dashboard.quickActions.documentation')} />
      </section>
    </>
  );
}

function greetingFor(now: Date, t: (key: string) => string): string {
  const h = now.getHours();
  if (h < 5) return t('dashboard.workingLate');
  if (h < 12) return t('dashboard.goodMorning');
  if (h < 17) return t('dashboard.goodAfternoon');
  if (h < 22) return t('dashboard.goodEvening');
  return t('dashboard.workingLate');
}

function tenantLabel(tenant: { isSuperAdmin: boolean; resellerId: string | null; customerId: string | null } | null, t: (key: string) => string): string {
  if (!tenant) return t('dashboard.noScope');
  if (tenant.isSuperAdmin) return t('dashboard.platformAdmin');
  if (tenant.customerId) return t('dashboard.customerScope');
  if (tenant.resellerId) return t('dashboard.resellerScope');
  return t('dashboard.unscoped');
}

function HeroStat({
  icon: Icon, label, value, sublabel, loading,
}: {
  icon: typeof PhoneCall;
  label: string;
  value: number;
  sublabel: string;
  loading: boolean;
}) {
  return (
    <div className="relative rounded-lg border-2 border-slate-900 bg-white p-6 overflow-hidden">
      <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none">
        <Icon className="h-32 w-32" strokeWidth={1} />
      </div>
      <div className="relative">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-2">
          {label}
        </div>
        <div className="text-6xl font-bold tabular-nums font-mono leading-none mb-3">
          {loading ? (
            <span className="inline-block h-14 w-24 bg-slate-100 animate-pulse rounded" />
          ) : (
            value.toLocaleString()
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {sublabel}
        </div>
      </div>
    </div>
  );
}

function InlineStat({
  icon: Icon, label, value, sublabel,
}: {
  icon: typeof PhoneCall;
  label: string;
  value: number;
  sublabel?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <Icon className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
            {label}
          </span>
        </div>
        <div className="text-2xl font-bold tabular-nums font-mono leading-none">
          {value.toLocaleString()}
        </div>
        {sublabel && (
          <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">
            {sublabel}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * LiveCallsStat — InlineStat variant for the "currently in progress"
 * call count. Has a pulsing red dot when there's at least one live call
 * to make it feel real-time. Auto-refreshes via the dashboard summary
 * query (10s). Scoped per role by RLS.
 */
function LiveCallsStat({ value }: { value: number }) {
  const isLive = value > 0;
  return (
    <Card className={cn(isLive && 'border-red-300 bg-red-50/50')}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <PhoneCall className={cn('h-3.5 w-3.5', isLive ? 'text-red-500' : 'text-slate-400')} />
            {isLive && (
              <span className="relative flex h-2 w-2" aria-hidden>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
            )}
          </div>
          <span className={cn(
            'text-[10px] uppercase tracking-widest font-mono',
            isLive ? 'text-red-700 font-semibold' : 'text-muted-foreground',
          )}>
            Live calls
          </span>
        </div>
        <div className={cn(
          'text-2xl font-bold tabular-nums font-mono leading-none',
          isLive && 'text-red-700',
        )}>
          {value.toLocaleString()}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">
          {isLive ? 'in progress now' : 'idle'}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Live now ──────────────────────────────────────────────────────────────────

/**
 * Shared 1-second wall clock for the ticking call durations. Called
 * from LiveNowList — which only mounts while there ARE live rows — so
 * one interval drives every row and an idle dashboard runs no timer
 * at all. (LiveNowSection itself can't own the hook: it stays mounted
 * and returns null when idle, which would leave the interval running.)
 */
function useNowTick(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Elapsed live-call duration — m:ss, rolling to h:mm:ss past an hour. */
function fmtLiveDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const sec = String(totalSec % 60).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${sec}`;
  return `${m}:${sec}`;
}

/** Pulsing "Live" pill — same markup as the call log's LivePill
 *  (frontend/src/app/portal/call-log/CallLogList.tsx), duplicated here
 *  because it isn't exported and the call-log page owns its own copy. */
function LiveNowPill() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
      </span>
      Live
    </span>
  );
}

/**
 * LiveNowSection — the calls behind the "Live calls" count. Polls
 * GET /call-sessions?active=true every 5s, but ONLY while the summary
 * count says something is in progress (react-query `enabled` flag), so
 * an idle dashboard adds zero extra requests. Hidden entirely when
 * nothing is live. Row click → /call-log, where the session drawer
 * lives.
 */
function LiveNowSection({ liveCount }: { liveCount: number }) {
  const enabled = liveCount > 0;
  const { data } = useQuery({
    queryKey: ['dashboard-live-calls'],
    queryFn: () => callSessions.list({ active: true, limit: 20 }),
    refetchInterval: 5_000,
    enabled,
  });
  // Gate rows on the live count too — when the last call ends, the
  // summary flips to 0 and the section disappears even though the
  // (now-disabled) list query still holds stale rows.
  const rows = enabled ? (data?.rows ?? []) : [];
  if (rows.length === 0) return null;
  return (
    <section className="mb-8" aria-label="Calls in progress right now">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
          Live now
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-700 flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden />
          {rows.length === 1 ? '1 call in progress' : `${rows.length} calls in progress`}
        </div>
      </div>
      <LiveNowList rows={rows} />
    </section>
  );
}

function LiveNowList({ rows }: { rows: CallSessionRow[] }) {
  const now = useNowTick(1_000);
  return (
    <div className="rounded-lg border-2 border-slate-900 bg-white overflow-hidden">
      <div
        className="hidden md:grid grid-cols-[4.25rem_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_5rem_4.5rem] items-center gap-3 px-4 py-2 border-b-2 border-slate-100 text-[10px] font-mono uppercase tracking-widest text-slate-400"
        aria-hidden
      >
        <span />
        <span>Caller</span>
        <span>Dialed</span>
        <span>Tree</span>
        <span className="text-end">Started</span>
        <span className="text-end">Duration</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((r) => <LiveCallRow key={r.id} row={r} now={now} />)}
      </ul>
    </div>
  );
}

function LiveCallRow({ row, now }: { row: CallSessionRow; now: number }) {
  const started = new Date(row.started_at);
  const startedLabel = started.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return (
    <li>
      <Link
        to="/call-log"
        title="Open the call log"
        className="grid grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[4.25rem_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_5rem_4.5rem] items-center gap-3 px-4 py-2.5 text-sm hover:bg-emerald-50/40 transition-colors"
      >
        <span><LiveNowPill /></span>
        <span className="min-w-0">
          {row.caller_display_name ? (
            <span className="block truncate text-slate-700" title={row.caller_display_name}>
              {row.caller_display_name}
            </span>
          ) : (
            <span className="block truncate font-mono text-slate-600">
              {row.from_number ?? 'Unknown caller'}
            </span>
          )}
          {row.caller_display_name && row.from_number && (
            <span className="block truncate font-mono text-[11px] text-slate-400">
              {row.from_number}
            </span>
          )}
        </span>
        <span className="hidden md:block min-w-0 truncate font-mono text-xs text-slate-500">
          {row.to_number ?? '—'}
        </span>
        <span className="hidden md:block min-w-0 truncate text-xs text-slate-600">
          {row.tree_name ?? '—'}
        </span>
        <span
          className="hidden md:block text-[11px] text-muted-foreground tabular-nums text-end whitespace-nowrap"
          title={started.toLocaleString()}
        >
          {startedLabel}
        </span>
        <span className="font-mono text-sm font-semibold tabular-nums text-emerald-700 text-end whitespace-nowrap">
          {fmtLiveDuration(now - started.getTime())}
        </span>
      </Link>
    </li>
  );
}

function AttentionCard({
  summary,
}: { summary: { voicemails_unread: number; forum_pending: number; orders_open: number } }) {
  const { t } = useTranslation();
  const items = [
    {
      count: summary.voicemails_unread,
      label: t('dashboard.unreadVoicemails'),
      icon: Mic,
      to: '/voicemails',
      tone: 'amber' as const,
    },
    {
      count: summary.forum_pending,
      label: t('dashboard.forumMessagesPending'),
      icon: Megaphone,
      to: '/forum',
      tone: 'rose' as const,
    },
    {
      count: summary.orders_open,
      label: t('dashboard.openOrders'),
      icon: ShoppingCart,
      to: '/orders',
      tone: 'sky' as const,
    },
  ];
  const hasAny = items.some((i) => i.count > 0);

  return (
    <aside>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-2">
        {t('dashboard.needsAttention')}
      </div>
      <div className="rounded-lg border-2 border-slate-900 bg-white overflow-hidden">
        {!hasAny ? (
          <div className="p-8 text-center">
            <Coins className="mx-auto h-6 w-6 text-slate-300 mb-2" />
            <p className="text-xs text-muted-foreground">
              {t('dashboard.inboxZero')}
            </p>
          </div>
        ) : (
          <ul className="divide-y-2 divide-slate-100">
            {items.map((it) =>
              it.count > 0 ? <AttentionItem key={it.label} item={it} /> : null,
            )}
          </ul>
        )}
      </div>
    </aside>
  );
}

function AttentionItem({
  item,
}: {
  item: { count: number; label: string; icon: typeof Mic; to: string; tone: 'amber' | 'rose' | 'sky' };
}) {
  const Icon = item.icon;
  const tones = {
    amber: 'bg-amber-50 text-amber-900 border-amber-300',
    rose: 'bg-rose-50 text-rose-900 border-rose-300',
    sky: 'bg-sky-50 text-sky-900 border-sky-300',
  }[item.tone];
  return (
    <li>
      <Link
        to={item.to}
        className="group flex items-center gap-3 px-3 py-3 hover:bg-slate-50/80 transition-colors"
      >
        <span className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded border',
          tones,
        )}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">
            <span className="font-mono tabular-nums">{item.count}</span> {item.label}
          </div>
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-all" />
      </Link>
    </li>
  );
}

function ActivityFeed({ items }: { items: RecentActivityItem[] }) {
  const { t } = useTranslation();
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
          {t('dashboard.activityFeed')}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          {t('dashboard.live')}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="rounded-lg border-2 border-slate-200 bg-white p-10 text-center">
          <Activity className="mx-auto h-6 w-6 text-slate-300 mb-2" />
          <p className="text-xs text-muted-foreground">{t('dashboard.noRecentActivity')}</p>
        </div>
      ) : (
        <div className="rounded-lg border-2 border-slate-900 bg-white overflow-hidden">
          <ul className="divide-y divide-slate-100">
            {items.map((it) => (
              <ActivityItem key={`${it.kind}-${it.id}`} item={it} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const KIND_META: Record<RecentActivityItem['kind'], { icon: typeof PhoneCall; color: string }> = {
  call:       { icon: PhoneCall, color: 'text-sky-600' },
  voicemail:  { icon: Mic, color: 'text-amber-600' },
  sms:        { icon: MessageSquare, color: 'text-violet-600' },
  order:      { icon: ShoppingCart, color: 'text-emerald-600' },
  enrollment: { icon: GraduationCap, color: 'text-indigo-600' },
};

function ActivityItem({ item }: { item: RecentActivityItem }) {
  const meta = KIND_META[item.kind];
  const Icon = meta.icon;
  const d = new Date(item.created_at);
  const rel = relativeTime(d);
  return (
    <li className="grid grid-cols-[2rem_minmax(0,1fr)_5.5rem] items-center gap-3 px-4 py-2.5 text-sm">
      <Icon className={cn('h-4 w-4', meta.color)} />
      <span className="truncate">{item.label}</span>
      <time className="text-[11px] text-muted-foreground tabular-nums text-right" dateTime={item.created_at}>
        {rel}
      </time>
    </li>
  );
}

function relativeTime(d: Date): string {
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function QuickAction({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="group rounded border border-slate-300 bg-white px-4 py-3 text-sm font-medium hover:border-slate-900 hover:shadow-sm transition-all flex items-center justify-between"
    >
      <span>{label}</span>
      <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}
